/**
 * <ParentFlowModal> — le gabarit d'étapes des gestes parent (template).
 *
 * Une feuille sous UN seul en-tête (`<Modal steps>`) : la question de l'étape en titre, le retour
 * à gauche, la croix à droite, et une barre fine à la place du filet quand le parcours a plus
 * d'une étape. Le nom du geste (`title`) ne s'affiche plus : il nomme la feuille pour un lecteur
 * d'écran, qui entend aussi « Étape 2 sur 3 ». Tous les gestes de l'accueil passent par là, pour
 * que « Quelle séance ? » ait la même allure d'un parcours à l'autre.
 *
 * `context` est la ligne qui rappelle de quoi on parle — « pour Damien », « Séance du jeudi 17
 * septembre à 18h00 » —, la première ligne du corps. Elle vaut mieux qu'un titre à rallonge.
 *
 * `errorMessage` est la place réservée à ce qui ne passe pas : un envoi refusé, ou un refus
 * attendu du serveur (« le quota d'absences a été atteint »). Une étape de geste garde sa
 * question à l'écran et dit pourquoi — elle ne se remplace pas par un dialogue.
 *
 * Coquille seule : aucune étape, aucune validation, aucun enchaînement. L'écran possède l'étape
 * courante et la passe.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { Modal } from "../../molecules/Modal"

export type ParentFlowModalProps = {
    visible: boolean
    onClose: () => void
    /** Nom du geste — « Décaler une séance ». Il n'est pas affiché : il nomme la feuille. */
    title: string
    /** Titre de l'étape courante, affiché dans l'en-tête — « Quelle séance ? ». */
    stepTitle: string
    step: number
    totalSteps: number
    /** Absent → pas de retour (première étape). */
    onBack?: () => void
    /** Ligne de rappel, en tête du corps. */
    context?: string
    /** Ce qui ne passe pas, posé entre le rappel et le corps de l'étape. */
    errorMessage?: string
    /** `alert` (défaut) pour un échec d'envoi, `warning` pour un refus attendu du serveur. */
    errorSeverity?: "alert" | "warning"
    /** Barre d'actions épinglée — « Annuler » et, s'il y a lieu, le bouton de validation. */
    footer?: React.ReactNode
    children: React.ReactNode
    testID?: string
}

export function ParentFlowModal(props: ParentFlowModalProps) {
    const {
        visible,
        onClose,
        title,
        stepTitle,
        step,
        totalSteps,
        onBack,
        context,
        errorMessage,
        errorSeverity = "alert",
        footer,
        children,
        testID,
    } = props

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={stepTitle}
            accessibilityLabel={title}
            steps={{ step, total: totalSteps, onBack }}
            fullScreenOnSmall
            sizeToContent
            dismissable
            footer={footer}
            testID={testID}
        >
            <Stack gap="4">
                {context != null && context !== "" && (
                    <Text size="text-sm" color="text-muted">
                        {context}
                    </Text>
                )}
                {errorMessage != null && errorMessage !== "" && (
                    <AlertCard severity={errorSeverity} message={errorMessage} />
                )}
                {children}
            </Stack>
        </Modal>
    )
}
